import * as Sentry from '@sentry/angular';
import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { App } from './app/app';
import { attachHttpFailureContext } from './app/core/monitoring/sentry-http-error';
import { captureGiftCardLink } from './app/shared/utils/gift-card-link';
import { captureEntryReferrer } from './app/shared/utils/return-origin';
import { environment } from './environments/environment';

/** Substituted at build time by esbuild's `define`, which the deploy workflow
 *  sets from the commit SHA (`.github/workflows/deploy-funnel.yaml`). Read
 *  through `typeof` rather than directly: a local `ng serve` or `ng test` build
 *  defines nothing, and an undefined identifier is a `ReferenceError`, not
 *  `undefined`. Those builds report as `dev`. */
declare const FUNNEL_RELEASE: string | undefined;

/** Monitoring and the app itself — started only on an address that no longer
 *  carries a gift-card code (see below). */
function startFunnel(): void {
  if (environment.sentry.enabled) {
    Sentry.init({
      dsn: environment.sentry.dsn,
      environment: environment.sentry.environment,
      // Without a release Sentry cannot say which deploy first saw an issue or
      // whether one regressed — "is this new?" is the first question asked of
      // every alert. Source maps are deliberately not uploaded; stack frames stay
      // minified.
      release: typeof FUNNEL_RELEASE === 'string' ? FUNNEL_RELEASE : 'dev',
      integrations: [
        Sentry.consoleLoggingIntegration(),
        // One failure, one issue. Angular's `provideBrowserGlobalErrorListeners()`
        // already forwards `window.onerror` and `unhandledrejection` into the
        // `ErrorHandler`, so leaving these on made the SDK catch the same throw a
        // second time — and worse, catch it as a bare `window.onerror` payload,
        // which is how the identical failure showed up twice as `<unknown>` and
        // `<anonymous>` with the same trace id. The Angular path is the one to
        // keep: it receives the original `HttpErrorResponse` object, which is what
        // `sentryErrorExtractor` needs to group it by endpoint.
        Sentry.globalHandlersIntegration({ onerror: false, onunhandledrejection: false }),
      ],
      // v11 collects request and response bodies, cookies, headers and the user's IP address unless told otherwise. This is
      // v10's default, which this app has always run with: no bodies, no cookies or IP address, headers and query
      // parameters without the ones that identify the user.
      dataCollection: {
        userInfo: false,
        cookies: false,
        httpHeaders: {
          request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
          response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
        },
        httpBodies: [],
        urlQueryParams: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
        genAI: { inputs: false, outputs: false },
        databaseQueryData: false,
        queues: false,
        graphQL: { document: false, variables: false },
      },
      beforeSend: attachHttpFailureContext,
    });
  }

  bootstrapApplication(App, appConfig).catch((err) => {
    // Reported explicitly: this rejection is caught, so it never becomes an
    // unhandled rejection for Angular's global listeners to forward — and they are
    // not installed yet anyway, since installing them is part of the bootstrap
    // that just failed. An app that never starts is the one failure nobody can
    // afford to miss.
    Sentry.captureException(err);
    console.error(err);
  });
}

// First of all: a gift-card code in the entry link is a live secret, so it leaves
// the address bar before Sentry can record the URL and before the router copies
// it into every navigation. See `shared/utils/gift-card-link.ts`.
const reloadWithoutGiftCard = captureGiftCardLink();

// Before the router runs: the referrer that matters is the one on the document
// the customer arrived on, and the landing route navigates to `/editor` as soon
// as it has a reservation. Also before a gift-card reload below, which replaces
// that document with one the funnel referred itself. See
// `shared/utils/return-origin.ts`.
captureEntryReferrer();

if (reloadWithoutGiftCard !== null) {
  // The browser would not take the code out of the address in place, and
  // starting on that address would hand it to Sentry and to the router. Load the
  // address without it instead; the code is already stored and applies after the
  // reload, which finds nothing left to take out and starts normally.
  window.location.replace(reloadWithoutGiftCard);
} else {
  startFunnel();
}
